import type { EapiClient, EapiEnvironment } from "./eapi-client";

export interface CustomFieldEntry {
  field_id: string;
  value: string;
}

interface DefinitionRow {
  id: string;
  display_name: string;
  field_type: string;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
const cache: Record<string, { fetchedAt: number; defs: DefinitionRow[] }> = {};

function extractDefinitions(payload: unknown): DefinitionRow[] {
  // The proxy wraps responses as { status, data }. The actual upstream body
  // may be `{ definitions: [...] }`, `{ custom_field_definitions: [...] }`,
  // or a top-level array — handle all defensively.
  const data = (payload as any)?.data ?? payload;
  const arr =
    (Array.isArray(data) && data) ||
    (Array.isArray(data?.definitions) && data.definitions) ||
    (Array.isArray(data?.custom_field_definitions) && data.custom_field_definitions) ||
    (Array.isArray(data?.results) && data.results) ||
    [];
  return arr.filter(
    (d: any) =>
      d && typeof d.id === "string" && typeof d.display_name === "string" && typeof d.field_type === "string",
  );
}

async function getDefinitions(
  client: EapiClient,
  environment: EapiEnvironment,
): Promise<DefinitionRow[] | null> {
  const cached = cache[environment];
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.defs;
  }
  try {
    const res = await client.getCustomFieldDefinitions();
    const defs = extractDefinitions(res);
    cache[environment] = { fetchedAt: Date.now(), defs };
    return defs;
  } catch (err) {
    console.warn("[eapi-custom-fields] definitions lookup failed; skipping custom_fields", err);
    return null;
  }
}

/** Normalizes a definition display name so "Trip type" matches "Trip Type". */
function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Best-effort lookup of EAPI custom field IDs for "Customer Name",
 * "Trailer ID", the operators and "Trip Type", plus assembly of the
 * `custom_fields` payload for trip create.
 * Returns `undefined` whenever anything is missing — callers should treat
 * this as "do not include custom_fields in the request".
 */
export async function buildCustomFieldsForOrder(
  client: EapiClient,
  environment: EapiEnvironment,
  args: {
    customerShortCode?: string | null;
    trailerId?: string | null;
    operator1Email?: string | null;
    operator2Email?: string | null;
    /** Order-level trip type, or the leg's own when assigned per leg. */
    tripType?: string | null;
  },
): Promise<CustomFieldEntry[] | undefined> {
  const defs = await getDefinitions(client, environment);
  if (!defs || defs.length === 0) return undefined;

  const findId = (displayName: string) => {
    const wanted = normalizeName(displayName);
    return defs.find(
      (d) => normalizeName(d.display_name) === wanted && d.field_type === "string",
    )?.id;
  };

  const entries: CustomFieldEntry[] = [];
  const customerId = findId("Customer Name");
  if (customerId && args.customerShortCode) {
    entries.push({ field_id: customerId, value: String(args.customerShortCode) });
  }
  const trailerFieldId = findId("Trailer ID");
  if (trailerFieldId && args.trailerId) {
    entries.push({ field_id: trailerFieldId, value: String(args.trailerId) });
  }
  const operator1FieldId = findId("Operator 1");
  if (operator1FieldId && args.operator1Email) {
    entries.push({ field_id: operator1FieldId, value: String(args.operator1Email) });
  }
  const operator2FieldId = findId("Operator 2");
  if (operator2FieldId && args.operator2Email) {
    entries.push({ field_id: operator2FieldId, value: String(args.operator2Email) });
  }
  const tripTypeFieldId = findId("Trip Type");
  const tripTypeValue = (args.tripType ?? "").toString().trim();
  if (tripTypeFieldId && tripTypeValue) {
    entries.push({ field_id: tripTypeFieldId, value: tripTypeValue });
  }

  return entries.length > 0 ? entries : undefined;
}

/** Test-only: reset the in-memory cache. */
export function __resetCustomFieldsCacheForTests() {
  for (const k of Object.keys(cache)) delete cache[k];
}